import { useEffect, useState } from "react";
import { getClaraviewComboimages } from "../../../Services/APIs/scan";
import Loader from "./loader";
import { useNavigate } from "react-router-dom";
import { requestPrice } from "../../../Services/APIs/report";
import { getLocalStorageJSON } from "../../../Utils/safeLocalStorage";
import { logEvent } from "../../../Utils/logEvent";

const Result = () => {
    const navigate = useNavigate();
    const company = localStorage.getItem("company") || "claraView";
    const uid = getLocalStorageJSON("usertpi").usertpi;
    const email = localStorage.getItem("ce")
    const [progress, setProgress] = useState(50); // Default position at 50%
    const [imageCropSrc, setCropImageSrc] = useState(null);
    const [imageComboSrc, setComboImageSrc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const imageName = JSON.parse(localStorage.getItem("response") || "{}").imageName;
    const [getPriceStatus, setGettingPrice] = useState("Get Pricing")
    const [priceError, setPriceError] = useState("")


    const handleSliderChange = (e) => {
        setProgress(e.target.value);
    };

    // Helper function for safe image data conversion
    const safelyProcessImageData = (imageData, setImageState, label) => {
        if (!imageData?.data) {
            logEvent({ app: company, uid, event: `${label} image data missing` });
            return;
        }

        try {
            // If it's already a string (possibly base64), use it directly
            if (typeof imageData.data === 'string') {
                setImageState(`data:image/jpeg;base64,${imageData.data}`);
                return;
            }

            // For ArrayBuffer/byte-array data, wrap it in a Blob rather than
            // base64-encoding it - avoids the ~33% size inflation and the
            // String.fromCharCode/btoa CPU cost of manual encoding.
            const bytes = new Uint8Array(imageData.data);
            const blob = new Blob([bytes], { type: 'image/jpeg' });
            setImageState(URL.createObjectURL(blob));
        } catch (err) {
            logEvent({ app: company, uid, event: `${label} image decode failed`, error: err });
        }
    };

    // Load and process images from API
    useEffect(() => {
        if (!uid || !imageName) {
            logEvent({ app: company, uid, event: "missing uid or image name" });
            setIsLoading(false);
            setError("Missing user ID or image name");
            return;
        }

        setIsLoading(true);
        const isEmailed = localStorage.getItem("ie") || false;
        const is_consent_given = JSON.parse(localStorage.getItem("is_consent_given") || "false");
        getClaraviewComboimages(uid, imageName, isEmailed, email, is_consent_given)
            .then((res) => {
                const { cropData, correctionData } = res.data.data;
                logEvent({ app: company, uid, event: "combo images request succeeded" });

                // Process each image separately with error handling - a
                // single image's decode failure doesn't set the page-level
                // `error`, so the other image still renders.
                safelyProcessImageData(cropData, setCropImageSrc, "crop");
                safelyProcessImageData(correctionData, setComboImageSrc, "combo");

                setIsLoading(false);
                localStorage.setItem("ie", "true")
            })
            .catch((err) => {
                setIsLoading(false);
                setError("Failed to load images");
            });

    }, [uid, imageName]);

    // Revoke Blob URLs once they're replaced or the component unmounts.
    useEffect(() => {
        return () => {
            if (imageCropSrc?.startsWith('blob:')) URL.revokeObjectURL(imageCropSrc);
            if (imageComboSrc?.startsWith('blob:')) URL.revokeObjectURL(imageComboSrc);
        };
    }, [imageCropSrc, imageComboSrc]);

    const getPrice = async () => {
        setPriceError("")
        setGettingPrice("Loading...")
        await requestPrice({ uid, email, company }).then((res) => {
            navigate("/claraview/thanks")
        }).catch((err) => {
            logEvent({ app: company, uid, event: "price request failed", error: err });
            setPriceError("Something went wrong")
        }).finally(() => {
            setGettingPrice("Get Price")
        })
    }

    return (
        <div className='h-dvh font-novatica flex flex-col'>
            {isLoading ? <Loader /> :
                <>
                    <div className='text-center px-2 pt-8 font-medium text-lg   tracking-wider text-[#2C2D24] md:text-3xl'>
                        Confidence Looks Good on You
                    </div>
                    <div className='text-center font-normal text-[12px] text-claraview-tertiary pb-4 md:text-xl mx-10 mt-3'>
                        This smile preview is just one idea of what’s possible. Your new smile may be more natural and subtle or more bold and bright. Think of it as a starting point to help you choose the smile that feels most like you.
                    </div>

                    {/* Image comparing Section (Flex Grow) */}
                    <div className="flex-grow m-4 flex flex-col">
                        <div className="image-slider-container flex-grow flex flex-col">
                            <div className="image-container flex-grow relative" style={{ height: '400px' }}>
                                <div
                                    className="stacked-images relative w-full h-full rounded-xl"
                                    style={{ maxHeight: '100%', overflow: "hidden" }}>

                                    {error ? (
                                        <div className="w-full h-full flex items-center justify-center text-red-500  ">{error}</div>
                                    ) : (
                                        <>
                                            <div className=" absolute top-0 left-0 w-full h-full rounded-xl">
                                                {imageCropSrc ? (
                                                    <div className="w-full h-full" style={{ position: 'relative' }}>
                                                        <img
                                                            src={imageCropSrc}
                                                            alt="After transformation"
                                                            className="w-full h-full object-contain"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center   md:text-xl">Image not available</div>
                                                )}
                                            </div>

                                            <div
                                                className="before-image absolute top-0 left-0 h-full"
                                                style={{
                                                    width: `${progress}%`,
                                                    overflow: 'hidden',
                                                    borderRight: '1px solid white'
                                                }}
                                            >
                                                {imageComboSrc ? (
                                                    <div className="w-full h-full" style={{ position: 'relative' }}>
                                                        <img
                                                            src={imageComboSrc}
                                                            alt="Before transformation"
                                                            className="absolute top-0 left-0 h-full object-contain"
                                                            style={{
                                                                width: `${100 / (progress / 100)}%`, // Scale proportionally to container
                                                                maxWidth: 'none'
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center   md:text-xl">Image not available</div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="mt-5 w-full px-4">
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={progress}
                                    onChange={handleSliderChange}
                                    className="slider w-full"
                                    style={{ "--progress": `${progress}%` }}
                                    disabled={isLoading || !!error}
                                />
                            </div>
                        </div>

                        <div className="px-5 pt-2 flex justify-between w-full text-claraview-tertiary">
                            <div className='  font-normal text-xs md:text-base '>Before</div>
                            <div className='  font-normal text-xs md:text-base '>After</div>
                        </div>
                    </div>

                    <div className='text-center font-normal   pb-2 text-[#2C2D24] text-base md:text-xl'>
                        Slide back and forth for smile options
                    </div>

                    <div className="mt-2 w-full px-8 text-center overflow-visible" >

                        <button
                            type="button"
                            className={`text-base font-medium rounded-md w-60 py-3 px-4 text-white bg-[#2C2D24] sm600:text-2xl tracking-wider ${getPriceStatus === "Loading..." ? "disabled" : ""} `}
                            onClick={getPrice}

                        >
                            {getPriceStatus}
                        </button>
                        {priceError && <p className="text-red-600">{priceError}</p>}

                    </div>

                    <div className='px-12 font-normal text-xs text-center   text-[#808F90] tracking-wider my-4 mb-8 md:text-base'>
                        AI-generated results for informational purposes only. Outcomes may vary. Consult a professional for personalized advice.
                    </div>
                </>
            }
        </div>

    );
}

export default Result;
